import { isRecord } from "../lib/records";

export type ApprovalToolKind =
  | "logMealItems"
  | "logMeal"
  | "logExercise"
  | "updateFoodMemory"
  | "deleteFoodMemory"
  | "generic";

export type ApprovalDisplay = {
  readonly kind: ApprovalToolKind;
  readonly title: string;
  readonly description: string;
  readonly currentCalories?: number;
  readonly totalCalories?: number;
  readonly calorieLabel?: string;
  readonly changedFields: readonly string[];
  readonly approveLabel: string;
  readonly approvedLabel: string;
  readonly canEdit: boolean;
  readonly canApprove: boolean;
  readonly editTemplate: string;
};

function readString(args: unknown, keys: readonly string[]): string | undefined {
  if (!isRecord(args)) return undefined;
  for (const key of keys) {
    const value = args[key];
    if (typeof value === "string" && value.trim().length > 0) return value;
  }
  return undefined;
}

function readNumber(args: unknown, keys: readonly string[]): number | undefined {
  if (!isRecord(args)) return undefined;
  for (const key of keys) {
    const value = args[key];
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
      return value;
    }
  }
  return undefined;
}

function readMemoryContext(args: unknown):
  | { readonly foodName: string; readonly calories: number }
  | undefined {
  if (!isRecord(args) || !isRecord(args.approvalContext)) return undefined;
  const foodName = args.approvalContext.foodName;
  const calories = args.approvalContext.calories;
  if (
    typeof foodName === "string" &&
    foodName.trim().length > 0 &&
    typeof calories === "number" &&
    Number.isFinite(calories) &&
    calories >= 0
  ) {
    return { foodName, calories };
  }
  return undefined;
}

function getToolKind(toolName: string): ApprovalToolKind {
  if (toolName.includes("logMealItems")) return "logMealItems";
  if (toolName.includes("logMeal")) return "logMeal";
  if (toolName.includes("logExercise")) return "logExercise";
  if (toolName.includes("updateFoodMemory")) return "updateFoodMemory";
  if (toolName.includes("deleteFoodMemory")) return "deleteFoodMemory";
  return "generic";
}

function getChangedFields(
  args: unknown,
  contextCalories?: number,
): readonly string[] {
  if (!isRecord(args)) return [];
  const fields: string[] = [];
  const currentCalories = contextCalories ?? readNumber(args, ["currentCalories"]);
  const calories = readNumber(args, ["calories"]);
  const servingSize = readString(args, ["servingSize"]);
  const notes = readString(args, ["notes"]);

  if (calories !== undefined) {
    fields.push(
      currentCalories !== undefined
        ? `Calories: ${currentCalories} -> ${calories}`
        : `Calories: ${calories}`,
    );
  }
  if (servingSize !== undefined) fields.push(`Serving: ${servingSize}`);
  if (notes !== undefined) fields.push("Notes updated");
  return fields;
}

export function getApprovalDisplay(
  toolName: string,
  args: unknown,
): ApprovalDisplay {
  const kind = getToolKind(toolName);
  const generalDescription =
    readString(args, ["description", "name", "type"]) ?? "this item";
  const memoryContext = readMemoryContext(args);
  const hasMemoryContext = memoryContext !== undefined;
  const memoryFoodName = memoryContext?.foodName;
  const currentCalories = memoryContext?.calories;

  switch (kind) {
    case "logMealItems": {
      const mealName = readString(args, ["name"]) ?? "Meal";
      const items = isRecord(args) && Array.isArray(args.items) ? args.items : [];
      const hasCompleteCalories =
        items.length > 0 &&
        items.every(
          (item) =>
            isRecord(item) &&
            typeof item.finalKcal === "number" &&
            Number.isFinite(item.finalKcal) &&
            item.finalKcal >= 0,
        );
      const totalCalories = hasCompleteCalories
        ? items.reduce(
            (total, item) => total + (isRecord(item) && typeof item.finalKcal === "number" ? item.finalKcal : 0),
            0,
          )
        : undefined;
      const itemLabel = `${items.length} ${items.length === 1 ? "item" : "items"}`;
      return {
        kind,
        title: "Log meal",
        description: mealName,
        totalCalories,
        calorieLabel: "Total calories",
        changedFields: [itemLabel],
        approveLabel: "Approve",
        approvedLabel: "Logged",
        canEdit: true,
        canApprove: hasCompleteCalories,
        editTemplate: `Actually, please adjust the items or calories for "${mealName}" before logging this meal.`,
      };
    }
    case "logMeal":
      const finalKcal = readNumber(args, ["finalKcal"]);
      return {
        kind,
        title: "Log meal",
        description: generalDescription,
        totalCalories: finalKcal,
        calorieLabel: "Final calories",
        changedFields: [],
        approveLabel: "Approve",
        approvedLabel: "Logged",
        canEdit: true,
        canApprove: finalKcal !== undefined,
        editTemplate: `Actually, please log "${generalDescription}" as ___ kcal instead of ${finalKcal ?? "___"} kcal.`,
      };
    case "logExercise":
      const finalBurnedKcal = readNumber(args, ["finalBurnedKcal"]);
      return {
        kind,
        title: "Log exercise",
        description: generalDescription,
        totalCalories: finalBurnedKcal,
        calorieLabel: "Final burned",
        changedFields: [],
        approveLabel: "Approve",
        approvedLabel: "Logged",
        canEdit: true,
        canApprove: finalBurnedKcal !== undefined,
        editTemplate: `Actually, please log "${generalDescription}" as ___ kcal burned instead of ${finalBurnedKcal ?? "___"} kcal.`,
      };
    case "updateFoodMemory":
      return {
        kind,
        title: "Update food memory",
        description: hasMemoryContext
          ? memoryFoodName ?? "Saved food details unavailable"
          : "Saved food details unavailable",
        currentCalories,
        totalCalories: readNumber(args, ["calories"]),
        calorieLabel: "Current calories",
         changedFields: getChangedFields(args, currentCalories),
        approveLabel: "Update",
        approvedLabel: "Updated",
        canEdit: hasMemoryContext,
        canApprove: hasMemoryContext,
        editTemplate: hasMemoryContext
          ? `Actually, update "${memoryFoodName ?? "saved food"}" to ___ kcal instead of ${currentCalories} kcal.`
          : "",
      };
    case "deleteFoodMemory":
      return {
        kind,
        title: "Forget food memory",
        description: hasMemoryContext
          ? memoryFoodName ?? "Saved food details unavailable"
          : "Saved food details unavailable",
        currentCalories,
        calorieLabel: "Current calories",
        changedFields: [],
        approveLabel: "Forget",
        approvedLabel: "Forgotten",
        canEdit: false,
        canApprove: hasMemoryContext,
        editTemplate: "",
      };
    case "generic":
      const totalCalories = readNumber(args, ["finalKcal", "finalBurnedKcal", "calories"]);
      return {
        kind,
        title: "Confirm action",
        description: generalDescription,
        totalCalories,
        changedFields: [],
        approveLabel: "Approve",
        approvedLabel: "Approved",
        canEdit: true,
        canApprove: true,
        editTemplate: `Actually, please correct "${generalDescription}".`,
      };
  }
}
